import { HostListener, Injectable, PipeTransform } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Howl, Howler } from 'howler';
import { MODERATORS, QDataObject, QUESTIONS } from './dataModels';
import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import { SortColumn, SortDirection } from './sortable.directive';
import { AData, ConnectSocketData, MeetingListEntry, ModeratorUser, QData, RoomConnectionState, ShowNames } from '@reflact/spftypes';
import { SocketService } from 'src/app/shared/SocketService';
import { NotificationService } from 'src/app/shared/NotificationService';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { UserListHelper } from '../UserListHelper';

interface SearchResult {
  questions: QData[];
  total: number;
}
export type FilterType = "all" | "open" | "new" | "my" | "done" | "deleted" | string;

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
}

const compare = (v1: string | number, v2: string | number) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

/**
 * Exact error message dialogue-server's ModeratorManager.selectMeeting returns
 * (via SelectMeetingResult.error) when the moderator's cached Adobe Connect
 * session itself has expired (AcSessionExpiredError) - as opposed to e.g. a
 * "no host/mini-host permission" error. There's no dedicated error code for
 * this in @reflact/spftypes, so WatchMeetingComponent matches on this exact
 * string to decide whether to redirect straight to /login instead of just
 * showing the error on the meeting picker. Keep in sync with ModeratorManager.
 */
export const AC_SESSION_EXPIRED_ERROR = "Deine Adobe-Connect-Anmeldung ist abgelaufen. Bitte melde dich in QA-External ab und wieder an, um sie zu erneuern.";

@Injectable({ providedIn: 'root' })
export class QuerstionService {
  public currentFilter: FilterType = "all";
  public isDisconnected: boolean = false;

  /**
   * Tracks whether restoreSession() (called once by AuthGuard on app start) has
   * resolved yet, and its outcome. A successful manual login (LoginComponent)
   * also sets this to 'authenticated' directly, so a subsequent restoreSession()
   * call is a no-op and doesn't race/overwrite it with a stale result.
   */
  public restoreStatus: 'pending' | 'authenticated' | 'unauthenticated' = 'pending';
  private restorePromise: Promise<void> | null = null;

  /**
   * Error message from the most recent rejoinScoId/joinMeeting call, if it
   * failed - surfaced by MeetingPickerComponent so a failed "Beobachten" click
   * doesn't just silently navigate to an empty question list.
   */
  public lastJoinError: string | undefined;

  public userListHelper: UserListHelper;

  public sidToTokenObject: Map<string, ConnectSocketData> = new Map();
  public sidToShowNames: Map<string, ShowNames> = new Map();
  public sidToConnectionState: Map<string, RoomConnectionState> = new Map();
  public sidToConnected: Map<string, boolean> = new Map();
  public audionotification: boolean = false;
  public desknotification: boolean = false;

  public moderatorsChanged = new Subject<void>();
  public showNamesChanged = new Subject<void>();

  private sound: Howl;
  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _questions$ = new BehaviorSubject<QData[]>([]);
  private _total$ = new BehaviorSubject<number>(0);

  private _state: State = {
    page: 1,
    pageSize: 100,
    searchTerm: '',
    sortColumn: 'creationTimestamp',
    sortDirection: 'desc'
  };
  constructor(private pipe: DecimalPipe, public socketService: SocketService, private notificationService: NotificationService) {
    this.userListHelper = new UserListHelper(this)

    this._search$.pipe(
      tap(() => this._loading$.next(true)),
      debounceTime(200),
      switchMap(() => this._search()),
      delay(200),
      tap(() => this._loading$.next(false))
    ).subscribe(result => {
      this._questions$.next(result.questions);
      this._total$.next(result.total);
    });

    this._search$.next();
    this.sound = new Howl({
      src: ['assets/bling2.mp3']
    });
    const notification = localStorage.getItem('QA-External-Notifications');
    if (notification) {
      const x = JSON.parse(notification);
      this.audionotification = x.audio;
      this.desknotification = x.desktop;
    }
    this.socketService.socket.on('connect', this.onConnect.bind(this));
    this.socketService.socket.on('disconnect', () => this.isDisconnected = true);

    this.socketService.socket.on('questionAdded', this.onQuestionAdded.bind(this));
    this.socketService.socket.on('workingOn', this.onWorkingOn.bind(this));
    this.socketService.socket.on('workingOff', this.onWorkingOff.bind(this));
    this.socketService.socket.on('questionAssigned', this.onQuestionAssigned.bind(this));
    this.socketService.socket.on('questionDeletedChanged', this.onQuestionDeletedChanged.bind(this));
    this.socketService.socket.on('answerAdded', this.onAnswerAdded.bind(this));
    this.socketService.socket.on('moderatorJoined', this.onModeratorJoined.bind(this));
    this.socketService.socket.on('moderatorLeft', this.onModeratorLeft.bind(this));
    this.socketService.socket.on('showNamesChanged', this.onShowNamesChanged.bind(this));
    this.socketService.socket.on('connectionStateUpdate', this.onConnectionStateUpdate.bind(this))
  }

  public get applicationMode(): "light" | "full" {
    return "full"
  }
  private async rejoinAfterReconnect() {
    const scoIds = Array.from(this.sidToTokenObject.values()).map(t => t.scoId);
    for (const scoId of scoIds) {
      await this.rejoinScoId(scoId);
    }
  }
  public async rejoinScoId(scoId: string): Promise<boolean> {
    console.debug('[QA-Debug] selectMeeting request', { scoId });
    const result = await this.socketService.selectMeeting(scoId);
    if (!result.success || !result.meeting) {
      console.warn('[QA-Debug] selectMeeting failed', { scoId, error: result.error });
      this.lastJoinError = result.error ?? 'Unbekannter Fehler beim Beobachten des Meetings';
      return false;
    }
    console.debug('[QA-Debug] selectMeeting succeeded', { scoId, sessionIdentifier: result.meeting.sessionIdentifier });
    this.lastJoinError = undefined;
    await this.applyJoinedMeeting(result.meeting);
    return true;
  }
  private getQuestionObjectFromId(questionId: string): QDataObject {
    const res = QUESTIONS.find(qo => qo.id === questionId);
    return res;
  }
  private onConnect() {
    console.log('connect now');
    this.isDisconnected = false;
    this.socketService.socket.sendBuffer = [];
    this.rejoinAfterReconnect();
  }

  public async loadMeetingList(): Promise<MeetingListEntry[]> {
    const meetings = await this.socketService.getMeetingList();
    console.debug('[QA-Debug] loadMeetingList result', meetings);
    return meetings;
  }

  /**
   * Restores the moderator login (and, if there was one, the previously-watched
   * meeting) from the server-side http session - called once by AuthGuard on
   * app start so a page refresh doesn't require logging in again. Safe to call
   * multiple times: only does actual work once (result cached in restoreStatus).
   */
  public restoreSession(): Promise<void> {
    if (this.restoreStatus !== 'pending') {
      return Promise.resolve();
    }
    if (!this.restorePromise) {
      this.restorePromise = this.doRestoreSession();
    }
    return this.restorePromise;
  }

  private async doRestoreSession(): Promise<void> {
    const result = await this.socketService.restoreModeratorSession();
    if (!result.success) {
      this.restoreStatus = 'unauthenticated';
      return;
    }
    this.restoreStatus = 'authenticated';
    if (result.watchedScoId) {
      await this.rejoinScoId(result.watchedScoId);
    }
  }

  /**
   * The scoId of the single meeting currently being watched, if any - only ever
   * one, see joinMeeting.
   */
  public getWatchedScoId(): string | undefined {
    return Array.from(this.sidToTokenObject.values())[0]?.scoId;
  }

  /**
   * The sessionIdentifier of the single meeting currently being watched, if any -
   * used to look up the live connected-participant count in userListHelper
   * (see UserListHelper.getUserListbySessionIdentifier), which is already kept
   * up to date in real time as pod sockets join/leave the room, independent of
   * whether they've ever submitted a question.
   */
  public getWatchedSessionIdentifier(): string | undefined {
    return Array.from(this.sidToTokenObject.keys())[0];
  }

  public isWatchingScoId(scoId: string): boolean {
    return Array.from(this.sidToTokenObject.values()).some(t => t.scoId === scoId);
  }

  /**
   * Claims/watches a meeting the moderator picked from the meeting list.
   * Replaces the old JWT-based joinModeratorClient - the server now returns
   * the joined ConnectSocketData directly (see ModeratorManager.selectMeeting),
   * so there is no token to decode anymore. Only one meeting can be watched at
   * a time - picking a new one automatically leaves any other one first.
   */
  public async joinMeeting(meeting: MeetingListEntry): Promise<boolean> {
    for (const other of Array.from(this.sidToTokenObject.values())) {
      if (other.scoId !== meeting.scoId) {
        await this.leaveMeeting(other.scoId);
      }
    }
    return this.rejoinScoId(meeting.scoId);
  }

  private async applyJoinedMeeting(joinData: ConnectSocketData) {
    this.sidToTokenObject.set(joinData.sessionIdentifier, joinData);
    this.userListHelper.myClientJoind(joinData.sessionIdentifier)
    this.sidToConnected.set(joinData.sessionIdentifier, true);
    this.getModeratorListCb(await this.socketService.moderatorList());
    this.socketService.socket.emit('getQuestionList', (questions: QData[], workedByArray: any[]) => this.getQuestionListCb(questions, workedByArray));
    const showNames = await this.socketService.getShowNames(joinData.sessionIdentifier)
    this.sidToShowNames.set(joinData.sessionIdentifier, showNames);
  }

  /**
   * Stops watching a meeting. Replaces the old JWT-based leaveModeratorClient -
   * releasing on the server also happens automatically on disconnect.
   */
  public async leaveMeeting(scoId: string) {
    const entry = Array.from(this.sidToTokenObject.entries()).find(([, t]) => t.scoId === scoId);
    if (!entry) {
      return;
    }
    const [sessionIdentifier] = entry;
    const worked = await this.socketService.deselectMeeting(scoId);
    if (worked) {
      this.userListHelper.myClientLeft(sessionIdentifier)
      const questions: QDataObject[] = QUESTIONS.filter(q => q.sessionIdentifier !== sessionIdentifier);
      QUESTIONS.splice(0);
      QUESTIONS.push(...questions);
      this._search$.next();
      this.sidToConnected.set(sessionIdentifier, false);
      this.sidToTokenObject.delete(sessionIdentifier);
    }
  }


  private onShowNamesChanged(showNames: ShowNames, sessionIdentifier: string) {
    console.log(sessionIdentifier, showNames)
    this.sidToShowNames.set(sessionIdentifier, showNames);
    this.showNamesChanged.next()
  }
  private onModeratorJoined(moderator: ModeratorUser) {

    this.checkIfInModeratorsArrayOrPush(moderator.userId, moderator.userName, moderator.sessionIdentifier)
    console.log('mod joined', moderator);
    //MODERATORS.push(moderator);
  }
  private onModeratorLeft(moderator: ModeratorUser) {
    const index = MODERATORS.findIndex(m => m.userId === moderator.userId && m.sessionIdentifier === moderator.sessionIdentifier);
    if (index > -1) {
      MODERATORS.splice(index, 1);
      this.moderatorsChanged.next()
    }
  }
  private getQuestionListCb(questions: QData[], workedByArray: { questionId: string, socketId: string, userName: string }[]) {
    console.log('q', questions);
    QUESTIONS.splice(0);
    questions.forEach(q => {
      QUESTIONS.push(new QDataObject(q));
      this.checkIfInModeratorsArrayOrPush(q.assignedToIdentifier, q.assignedToName, q.sessionIdentifier);
    });
    workedByArray.forEach(w => {
      this.onWorkingOn(w.questionId, w.socketId, w.userName);
    });
    this._search$.next();
  }
  private getModeratorListCb(moderators: ModeratorUser[]) {
    console.log('mpd', moderators);
    moderators.forEach(mod => {
      this.checkIfInModeratorsArrayOrPush(mod.userId, mod.userName, mod.sessionIdentifier);
    });
  }
  private onQuestionAssigned(questionId: string, assignedUserId: string, assignedUserName: string) {
    console.log("RECIEVED QUESTION ASSIGNED", assignedUserName + " " + questionId + ":" + new Date())
    const q = this.getQuestionObjectFromId(questionId);
    q.assignedTo(assignedUserId);
    q.assignToName(assignedUserName);
    this.checkIfInModeratorsArrayOrPush(assignedUserId, assignedUserName, q.sessionIdentifier);
    if (assignedUserId === this.sidToTokenObject.get(q.sessionIdentifier).userId) {
      this.playSoundIfAudioOn();
      this.sendNotificationIfAllowed('Ihnen wurde eine Frage zugewiesen.');
    }
  }
  private checkIfInModeratorsArrayOrPush(id: string, name: string, sessionIdentifier: string) {
    if (!id || id === '' || !name || name === '') return;
    if (!MODERATORS.some(m => m.userId === id && m.sessionIdentifier === sessionIdentifier)) {
      MODERATORS.push({ userId: id, userName: name, sessionIdentifier: sessionIdentifier });
      this.moderatorsChanged.next()
    }
  }
  private onQuestionDeletedChanged(questionId: string, deleted: boolean) {
    const q = this.getQuestionObjectFromId(questionId);
    if (deleted) {
      q.delete();
    } else {
      q.unDelete();
    }
    this._search$.next();
  }
  private onAnswerAdded(answer: AData, question?: Omit<QData, "answers">) {
    const found = QUESTIONS.find(qo => qo.id === question._id);
    console.log('new anwer', found);
    if (found) {
      found.addAnswer(answer);
    }
  }
  private onWorkingOn(questionId: string, socketId: string, userName: string) {
    const found = QUESTIONS.find(qo => qo.id === questionId);
    if (found) {
      const u = {
        socketId,
        userName
      };
      if (!found.workedByArray.some(w => w.socketId === u.socketId)) found.workedByArray.push(u);
    }
  }
  private onWorkingOff(questionId: string, socketId: string, userName: string) {
    const found = QUESTIONS.find(qo => qo.id === questionId);
    if (found) {
      const index = found.workedByArray.findIndex(w => w.socketId === socketId)
      if (index > -1) found.workedByArray.splice(index, 1);
    }
  }
  private onQuestionAdded(question: QData) {
    const questionObject: QDataObject = new QDataObject({
      _id: question._id,
      creatorIdentifier: question.creatorIdentifier,
      creatorName: question.creatorName,
      creationTimestamp: question.creationTimestamp,
      text: question.text,
      assignedToIdentifier: question.assignedToIdentifier,
      isDeleted: question.isDeleted,
      answers: question.answers,
      sessionIdentifier: question.sessionIdentifier,
    });
    QUESTIONS.push(questionObject);
    this._search$.next();
    this.playSoundIfAudioOn();
    this.sendNotificationIfAllowed('Es wurde eine neue Frage gestellt.');
  }
  get questions$() { return this._questions$.asObservable(); }
  get total$() { return this._total$.asObservable(); }
  get loading$() { return this._loading$.asObservable(); }
  get page() { return this._state.page; }
  get pageSize() { return this._state.pageSize; }
  get searchTerm() { return this._state.searchTerm; }

  set page(page: number) { this._set({ page }); }
  set pageSize(pageSize: number) { this._set({ pageSize }); }
  set searchTerm(searchTerm: string) { this._set({ searchTerm }); }
  set sortColumn(sortColumn: SortColumn) { this._set({ sortColumn }); }
  set sortDirection(sortDirection: SortDirection) { this._set({ sortDirection }); }

  public getNonPaginatedFilteredquestions(): QDataObject[] {

    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;
    let questions = [...QUESTIONS]
    questions = questions.filter(question => this.matchFilter(question, this.currentFilter));
    // 1. sort
    questions = this.sort(questions, sortColumn, sortDirection);

    // 2. filter
    questions = questions.filter(question => this.matches(question, searchTerm, this.pipe));
    console.log("currentfilter", { cf: this.currentFilter, QUESTIONS, questions })
    return questions
  }
  filter(type: FilterType) {
    this.currentFilter = type;
    this._search$.next();
  }

  public countFilter(type: FilterType) {
    let questions = [...QUESTIONS]
    questions = questions.filter(question => this.matchFilter(question, type));
    return questions.length
  }

  public answer(question: QDataObject, text: string, isPublicAnswer: boolean = false) {
    this.socketService.socket.emit('newAnswer', question.id, text, isPublicAnswer);
  }
  delete(question: QDataObject) {
    this.socketService.socket.emit('questionDeletedChanged', question.id, true);
  }
  unDelete(question: QDataObject) {
    this.socketService.socket.emit('questionDeletedChanged', question.id, false);
  }
  questionAssigned(question: QDataObject, assignedUserId: string, assignedUserName: string) {
    console.log("EMIT QUESTION ASSIGNED", assignedUserName + " " + question.id + ":" + new Date())
    this.socketService.socket.emit('questionAssigned', question.id, assignedUserId, assignedUserName);
  }
  workingOn(question: QDataObject) {
    this.socketService.socket.emit('workingOn', question.id);
  }
  workingOff(question: QDataObject) {
    this.socketService.socket.emit('workingOff', question.id);
  }
  broadcastMessage(sessionIdentifier: string, message: string) {
    console.log('broadcast', sessionIdentifier, message);
    this.socketService.socket.emit('broadcastMessage', sessionIdentifier, message);
    return true;
  }
  public setShowNames(sessionIdentifier: string, showNames: ShowNames) {
    this.socketService.socket.emit('setShowNames', sessionIdentifier, showNames);
  }
  public getQuestionsFromSameUser(question: QData) {
    let questions = [...QUESTIONS];
    questions = questions.filter(q => q.creatorName == question.creatorName);
    questions.sort(this.sortByTime.bind(this));
    return questions;
  }
  private sortByTime(a: QDataObject, b: QDataObject): number {
    return a.lastTime - b.lastTime;
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    console.log('state', this._state);
    this._search$.next();
  }

  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;
    let questions = [...QUESTIONS]
    questions = questions.filter(question => this.matchFilter(question, this.currentFilter));
    // 1. sort
    questions = this.sort(questions, sortColumn, sortDirection);

    // 2. filter
    questions = questions.filter(question => this.matches(question, searchTerm, this.pipe));
    const total = questions.length;

    // 3. paginate
    questions = questions.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
    return of({ questions, total });
  }
  private matchFilter(question: QDataObject, type: FilterType): boolean {

    switch (type) {
      case "all":
        if (question.isDeleted) { return false }
        return true;
      case "open":
        if (question.isDeleted) { return false }
        if (question.isAnswered) { return false }
        return true;
      case "new":
        if (question.isDeleted) { return false }
        if (question.isAnswered) { return false }
        if (question.assignedToIdentifier) { return false }
        return true;
      case "my":
        if (question.isDeleted) { return false }
        if (question.isAnswered) { return false }
        if (this.sidToTokenObject.has(question.sessionIdentifier) && this.sidToTokenObject.get(question.sessionIdentifier).userId != question.assignedToIdentifier) { return false }
        return true;
      case "done":
        if (question.isDeleted) { return false }
        if (question.isAnswered) { return true }
        return false;


      case "deleted":
        return question.isDeleted


      default:
        return false;


    }
  }

  public getSessionIdentifierNamedByToken(token: ConnectSocketData): string {

    if (token) {
      return token.sessionName + "@" + this.trimConnectDomain(token.connectDomain);
    }
    return '';
  }


  public getSessionIdentifierNamedByQuestion(question: QData): string {
    const token = this.sidToTokenObject.get(question.sessionIdentifier);
    return this.getSessionIdentifierNamedByToken(token)
  }

  public trimConnectDomain(connectDomain: string): string {
    try {
      return connectDomain.replace("https://", "").replace("http://", "").replace(".reflact.com", "").replace(".adobeconnect.com", "")
    } catch (e) { }
    return ""
  }

  private sort(questions: QDataObject[], column: SortColumn, direction: string): QDataObject[] {
    if (direction === '' || column === '') {
      return questions;
    } else {
      return [...questions].sort((a, b) => {
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    }
  }
  private matches(question: QDataObject, term: string, pipe: PipeTransform) {
    return question.text.toLowerCase().includes(term.toLowerCase())

  }
  public playSoundIfAudioOn() {
    if (this.audionotification) this.sound.play();
  }
  public sendNotificationIfAllowed(title: string, options?: NotificationOptions) {
    if (this.desknotification && this.notificationService.notificationsPossible && Notification.permission === 'granted') {
      new Notification(title, options);
    }
  }
  private onConnectionStateUpdate(roomState: RoomConnectionState, sessionIdentifier: string) {
    this.sidToConnectionState.set(sessionIdentifier, roomState);
  }
}